import { formatDate } from '@angular/common';
import { Component, ElementRef, HostBinding, ViewChild, OnDestroy } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { Endpoint, HelpIndex, LocalStorageKey, SettingsType, UpdateStatusType, StatusCode, MessageType, MembershipType } from '../../../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, GenericColDef, GridContext, SortByFilter, FilterTemplateType } from '../../../../models/grid-context';
import * as _ from 'underscore';
import { Project, ProjectActivationState, ProjectState } from '../../../../models/project';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { MembershipModalComponent } from '../../membership-modal/membership-modal.component';
import { ChangeServiceModalComponent } from '../../change-service-modal/change-service-modal.component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { Subscription } from 'rxjs';
import { ActionType, ObjectActionListType, PermissionSet, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-projects',
    templateUrl: './projects.component.html',
    styleUrls: ['./projects.component.scss']
})
export class ProjectsComponent implements SettingsNavigationContentComponent, OnDestroy {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    automationId = 'projects-list';
    isDirty: boolean;
    validChanges: boolean;
    gridName = 'Projects';
    private entityChangedEvent: Subscription;

    isBusy = false;
    isBusyMessage = 'Loading Projects...';
    helpIndex = HelpIndex.sys_projects_dlg;
    updateStatusType = UpdateStatusType.PROJECT;

    // permissions
    hasProjectsAddEditPermissions: boolean;
    hasContextMenuPermissions = true;

    allUsers = [];
    roleList = [];
    services: Array<any>;
    gridContext = new GridContext({
        sortTypeEnabled: true,
        sortEnabled: true,
        sortAscending: true,
        sortAscendingFieldName: 'descending',
        sortByFieldName: 'sortByFilter',
        sortBy: SortByFilter.NAME
    });

    requestInfo = {
        endpoint: Endpoint.realmManager_listProjects,
        responseName: 'projects',
        data: { contextHandle: 'noHandle', filters: [], systemScope: true }
    };

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Project Name',
            field: 'name',
            lockVisible: true,
            automationId: this.automationId,
            minWidth: 60, width: 200,
            cellTemplateType: CellTemplateType.disabledLinkLabel,
            sortBy: SortByFilter.NAME,
            defaultSortBy: SortByFilter.NAME,
            defaultSortAscending: true,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: "PROJECT",
            suppressMovable: true,
            reviewIcon: true,
            claimsIcon: true
        },
        {
            headerName: 'Project Description',
            field: 'description',
            automationId: this.automationId,
            minWidth: 60, width: 310,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: "DESCRIPTION",
            suppressMovable: true,
        },
        {
            headerName: 'Organization',
            field: 'orgName',
            automationId: this.automationId,
            minWidth: 60, width: 200,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            sortBy: SortByFilter.ORG,
            filterType: FilterTemplateType.comboBox,
            serviceTierFilter: true,
            filterAttributeName: "org",
            suppressMovable: true,
        },
        {
            headerName: 'Project State',
            field: 'projectActivationState',
            automationId: this.automationId,
            minWidth: 60, width: 170,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            sortBy: SortByFilter.STATUS,
            filterType: FilterTemplateType.singleChoice,
            serviceTierFilter: true,
            suppressMovable: true,
            filterAttributeName: 'STATUS'
        },
        {
            headerName: 'Service',
            field: 'projectServiceName',
            automationId: this.automationId,
            minWidth: 60, width: 170,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            sortBy: 'SERVICE',
            filterType: FilterTemplateType.singleChoice,
            serviceTierFilter: true,
            suppressMovable: true,
        },
        {
            headerName: 'Created By',
            field: 'userName',
            automationId: this.automationId,
            minWidth: 60, width: 170,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            sortBy: SortByFilter.CREATED_BY,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: SortByFilter.CREATED_BY,
            suppressMovable: true,
        },
        {
            headerName: 'Date Created',
            field: 'dateCreated',
            automationId: this.automationId,
            minWidth: 60, width: 145,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellTemplateType: CellTemplateType.date,
            sortBy: SortByFilter.DATE_CREATED,
            filterType: FilterTemplateType.date,
            serviceTierFilter: true,
            filterAttributeName: "CREATED_TIME",
            suppressMovable: true,
        },
        {
            headerName: 'Date Last Accessed',
            field: 'lastAccessed',
            automationId: this.automationId,
            minWidth: 60, width: 145,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellTemplateType: CellTemplateType.date,
            sortBy: 'LAST_ACCESSED',
            filterType: FilterTemplateType.date,
            serviceTierFilter: true,
            filterAttributeName: "LAST_ACCESSED_TIME",
            suppressMovable: true,
        },
        {
            headerName: 'Project ID',
            field: 'handle',
            automationId: this.automationId,
            minWidth: 60, width: 100,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip
        },
        {
            headerName: 'Review Project',
            field: 'reviewProjects',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.reviewProjectsWithTooltip,
            filterType: FilterTemplateType.freeform,
            serviceTierFilter: true,
            filterAttributeName: "REVIEW_PROJECT_NAME",
            minWidth: 200, width: 250,
            addReviewTemplate: true
        },
        {
            headerName: 'Project GUID',
            field: 'projectGuid',
            automationId: this.automationId,
            minWidth: 60, width: 200,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            hide: true
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        }
    );

    contextMenuOptions = [
        new ContextMenuOption({
            nameFunction: (item) => `Enable All Projects in ${item.orgName}`,
            iconClass: 'fa-book',
            iconColor: 'color-blue-01',
            automationId: 'project-list-enable-all',
            click: (item) => this.changeAll(item, true)
        }),
        new ContextMenuOption({
            nameFunction: (item) => `Disable All Projects in ${item.orgName}`,
            iconClass: 'fa-book',
            iconColor: 'color-grey-08',
            automationId: 'project-list-disable-all',
            click: (item) => this.changeAll(item, false)
        }),
        new ContextMenuOption({
            divider: true
        }),
        new ContextMenuOption({
            name: 'Add/Remove Members ...',
            iconClass: 'fa-users',
            iconColor: 'color-blue-01',
            automationId: 'project-list-add-remove-members',
            click: (item) => this.addRemoveMembers(item)
        }),
        new ContextMenuOption({
            divider: true
        }),
        new ContextMenuOption({
            name: 'Change Service ...', iconClass: 'fa-code-fork', iconColor: 'color-blue-01',
            automationId: 'project-list-change-service',
            click: (item) => this.changeService(item)
        }),
        new ContextMenuOption({
            name: 'Delete Project', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: 'projects-list-delete-project',
            click: (item) => this.deleteProject(item),
            enabled: (item) => item.projectActivationState !== ProjectActivationState.DELETE_REQUEST_PENDING && item.projectActivationState !== ProjectActivationState.DELETE_PENDING,
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.PROJECTS, permissionActions: [ActionType.DELETE] })]
        })
    ];

    constructor(private commonService: CommonService, private restService: RestService) {
        const promiseList = [];
        const statusCodeColDef = this.columnDefs.find(f => f.field === 'projectActivationState');
        statusCodeColDef.filterItems = ['All', ...this.commonService.getEnumNames(StatusCode).sort()];
        statusCodeColDef.filterItemSelected = 'All';
        promiseList.push(this.restService.post(Endpoint.realmManager_listOrganizations, { count: 0, startIndex: 0, filters: [], systemScope: true }).then(response => {
            const orgNameColDef = this.columnDefs.find(f => f.field === 'orgName');
            if (response?.organizations && orgNameColDef) {
                orgNameColDef.filterItems = response.organizations.map(q => q.name).sort();
                if (this.genericGrid) {
                    this.genericGrid.setColumnDefs(this.columnDefs);
                    this.genericGrid.refreshColumns();
                }
            }
        }));
        this.entityChangedEvent = this.commonService.getMessage(MessageType.updateSystemSettingsProjectList).subscribe((data) => {
            this.genericGrid.loadData();
        });
    }

    responseCallback = (response: any) => {
        if (!this.services) {
            const serviceColDef = this.columnDefs.find(f => f.field === 'projectServiceName');
            const serviceList = []
            if (response?.services && serviceColDef) {
                response.services.forEach(service => {
                    serviceList.push(service.serviceName + ' (' + service.nodeCount + ' Nodes, ' + service.projectCount + ' Projects, ' + service.activeJobCount + ' Jobs)');
                });
                serviceColDef.filterItemSelected = 'All';
                serviceColDef.filterItems = ['All', ...serviceList.sort()];
                if (this.genericGrid) {
                    this.genericGrid.setColumnDefs(this.columnDefs);
                }
                setTimeout(() => {
                    this.genericGrid.refreshColumns();
                });
            }
        }
        this.services = response?.services;
    }

    responseArrayCallback = (value: any) => {
        if (_.map(this.commonService.currentUser.projects, project => project.handle).includes(value.handle) && value.projectActivationState === ProjectActivationState.ACTIVE && (value.projectState === ProjectState.AVAILABLE || value.projectState === ProjectState.WARNING)) {
            value.accessAllowed = true; // Enables link in column
        }
        if (value?.dateCreated?.length > 0) {
            value.dateCreated = formatDate(value.dateCreated, 'yyyy-MM-dd HH:mm:ss', 'en-US');
        }
        if (value?.lastAccessed?.length > 0) {
            value.lastAccessed = formatDate(value.lastAccessed, 'yyyy-MM-dd HH:mm:ss', 'en-US');
        }
        return value;
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType): void => {
        if (rowNode?.data && action === CellActionType.linkLabelClick) {
            this.openProjectInNewSession(rowNode.data.handle);
        }
    }

    openProjectInNewSession(prjHandle: string): void {
        this.commonService.updateAppContextAndStoreIt(prjHandle);
        window.open(window.location.href, '_blank');
    }

    download(): void {
        this.restService.post(Endpoint.realmManager_getAllProjectsDownloadUrl, { systemScope: true }).then(response => {
            if (response?.url) {
                this.commonService.unzipFile(response.url, this.downloadLink.nativeElement, 'ProjectsManifest');
            }
        })
    }

    parseUserOrGroup(entity: any): any {
        if (entity) {
            if (entity.userName) {
                entity.type = entity.importedFromSystemUser ? 'System User' : 'User';
                entity.iconClass = `fa-user ${entity.importedFromSystemUser ? ' color-purple-03' : ' color-yellow-07'}`;
                entity.name = entity.userName;
            } else {
                entity.type = entity.importedFromSystemGroup ? 'System Group' : 'Group';
                entity.iconClass = `fa-users ${entity.importedFromSystemGroup ? ' color-purple-03' : ' color-yellow-07'}`;
            }
            if (this.roleList) {
                entity.roleList = this.roleList;
                entity.initialRole = entity.selectedRole = this.getUserRole(entity.roleHandles);
            }
        }
    }

    getUserRole(roleHandles: Array<string>): any {
        if (roleHandles && roleHandles.length > 0) {
            return this.roleList.filter(r => r.handle === roleHandles[0]).pop();
        }
        return null;
    }


    changeAll(project: any, enable: boolean): void {
        this.restService.post(Endpoint.orgManager_updateProjectsState,
            { organizationHandle: project.orgName, projectState: enable ? 'ACTIVE' : 'DISABLED', systemScope: true });
    }

    addRemoveMembers(project): void {
        if (project) {
            this.isBusy = true;
            this.isBusyMessage = 'Loading ...';
            this.restService.post(Endpoint.adminOrgManager_listRoles, {
                objectType: 'PROJECT',
                organizationName: project.orgName,
                systemScope: true
            }).then(responseListRoles => {
                this.roleList = responseListRoles?.roles;

                this.restService.post(Endpoint.adminOrgManager_listUsersAndGroups, { organizationName: project.orgName, systemScope: true }).then(responseListUsersAndGroups => {
                    if (responseListUsersAndGroups) {
                        this.allUsers = [];
                        if (responseListUsersAndGroups.groups) {
                            responseListUsersAndGroups.groups.forEach(entity => {
                                this.parseUserOrGroup(entity);
                                this.allUsers.push(entity);
                            });
                        }
                        if (responseListUsersAndGroups.users) {
                            responseListUsersAndGroups.users.forEach(entity => {
                                this.parseUserOrGroup(entity);
                                this.allUsers.push(entity);
                            });
                        }
                    }
                    this.restService.post(Endpoint.permissionManager_getPermission, { objectHandle: project.handle, systemScope: true }).then((response) => {
                        if (response?.permissionMap) {
                            const members = [];
                            if (response.permissionMap.groupRoleSetList) {
                                response.permissionMap.groupRoleSetList.forEach(entity => {
                                    const groupInfo = this.allUsers.find(u => u.name.toLowerCase() === entity.name.toLowerCase() && u.orgName === project.orgName);
                                    if (groupInfo) {
                                        members.push(groupInfo);
                                    }
                                });
                            }
                            if (response.permissionMap.userRoleSetList) {
                                response.permissionMap.userRoleSetList.forEach(entity => {
                                    const userInfo = this.allUsers.find(u => u.handle === `${entity.name}@${project.orgName}`);
                                    if (userInfo) {
                                        members.push(userInfo);
                                    }
                                });
                            }
                            this.isBusy = false;
                            this.commonService.openModal(MembershipModalComponent, {
                                class: 'vertical-percent-80',
                                modalData: { leftList: this.allUsers, rightList: members.sort(this.compare), membershipType: MembershipType.project, isSystemSettingsProjects: true, isSystemSettingsContext: true, projectName: project.name, project: project }
                            });
                        }
                    }, () => {
                        this.isBusy = false;
                    });
                }, () => {
                    this.isBusy = false;
                });
            }, () => {
                this.isBusy = false;
            });
        }
    }

    private compare(a: any, b: any) {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        if (aName < bName) {
            return -1;
        } else if (aName > bName) {
            return 1;
        }
        return 0;
    }

    changeService(project): void {
        this.commonService.openModal(ChangeServiceModalComponent, {
            modalData: {
                project: new Project(project),
                services: this.services,
                currentService: project.projectServiceName
            },
            class: 'vertical-px-500 horizontal-px-1200'
        });
    }

    deleteProject(project): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: new Project(project), options: { deleteProjectFromSystemSettings: true } });
    }

    saveChanges(): Promise<boolean> {
        return Promise.resolve(true);
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.hasProjectsAddEditPermissions = Permissions.checkUserPermission(ObjectActionListType.PROJECTS, ActionType.EDIT);
        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasProjectsAddEditPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }
        return;
    }

    ngOnDestroy(): void {
        this.entityChangedEvent.unsubscribe();
    }

}